

















































import Vue from "vue";
import GoogleMapsApiLoader from "google-maps-api-loader";
import LayerSelector from "./LayerSelector.vue";
import SearchBox from "./SearchBox.vue";
import SearchPlot from "./SearchPlot.vue";
import DistanceCalculator from "./DistanceCalculator.vue";
import {
  mapSettings,
  CIRLE_MARKER_CONFIG,
  POLY_CONFIG,
} from "./config/mapSettings";
import { mapState, mapMutations } from "vuex";
import { SET_SELECTED_PLACE, SET_MAP_LOADED, RootState } from "@/store/types";
import proj4 from "proj4";
import { createRouteLayer } from "./routeLayer";

export default Vue.extend({
  components: {
    LayerSelector,
    SearchBox,
    DistanceCalculator,
    SearchPlot,
  },

  data() {
    return {
      apiKey: "AIzaSyBPVhvP83qRwYiYx54QYSqgfa2gAdHjU3o",
      google: null,
      map: null,
      geocoder: null,
      marker: null,
      poly: null,
      distanceMarker: null,
      measurementMode: false,
      circle: null,
      kmlLayers: [],
      routeError: "",
      overlayWms: null, // Przechowuje instancję ImageMapType (kafelki)
      polygonLayer: null,
      isWMSLoading: false,
      wmsError: false,
    };
  },

  async mounted() {
    /** Inicjalizacja biblioteki Google Maps */
    this.google = await GoogleMapsApiLoader({
      libraries: ["places"],
      apiKey: this.apiKey,
    });
    this.initializeMap();
    this.$nextTick(this.setMapLoaded(true));
  },

  beforeDestroy() {
    this.kmlLayers.forEach(layer => layer.setMap(null));
  },

  computed: {
    ...mapState({
      survey: (state) => (state as RootState).survey,
      layers: (state) => (state as RootState).survey.layers,
      circleRadius: (state) => (state as RootState).mapCircleRadius,
    }),
  },

  watch: {
    /** Reaguje na zmianę promienia okręgu w panelu bocznym */
    circleRadius(): void {
      if (this.circle) {
        this.circle.setRadius(this.circleRadius);
      }
    },
  },

  methods: {
    ...mapMutations({
      setSelectedPlace: SET_SELECTED_PLACE,
      setMapLoaded: SET_MAP_LOADED,
    }),

    /** * Konwertuje współrzędne geograficzne (WGS84) na metry układu Web Mercator.
     * Niezbędne do poprawnego wyznaczenia BBOX dla każdego kafle WMS.
     */
    latLngToEPSG3857(lat: number, lng: number): number[] {
      const x = (lng * 20037508.34) / 180;
      let y =
        Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
      y = (y * 20037508.34) / 180;
      return [x, y];
    },

    /** Obsługuje włączanie/wyłączanie warstw KML z panelu wyboru */
    handleLayerSelect(payload: { id: number; value: boolean }): void {
      const { id, value } = payload;
      value ? this.addLayerToMap(id) : this.removeLayerFromMap(id);
    },

    /** Główna funkcja ustawiająca mapę, listenery i bazowe warstwy KML */
    initializeMap(): void {
      const mapContainer = this.$refs.googleMap;
      let geoLoc = false;
      if (this.survey.geoLoc) {
        geoLoc = this.survey.geoLoc.split(",");
      }

      this.map = new this.google.maps.Map(mapContainer, {
        ...mapSettings,
        ...(geoLoc && {
          center: new this.google.maps.LatLng(geoLoc[0], geoLoc[1]),
          zoom: Number(this.survey.zoom),
        }),
        zoomControlOptions: {
          position: this.google.maps.ControlPosition.RIGHT_TOP,
        },
      });

      this.geocoder = new this.google.maps.Geocoder();

      /** Ładowanie warstw KML zdefiniowanych w obiekcie survey */
      if (this.layers && this.layers.length > 0) {
        for (const layer of this.layers) {
          const route = createRouteLayer(this.google, this.map, layer, !geoLoc);
          this.kmlLayers.push(route);
          route.ready.catch(error => {
            this.routeError = "Nie udało się wczytać przebiegu linii: " + layer.name;
            // eslint-disable-next-line no-console
            console.error('Route loading failed', layer.id, error);
          });
        }
      }

      this.map.addListener("click", (e) => this.onMapClick(e.latLng));

      /** Listener sprawdzający Zoom i zarządzający warstwą kafelkową WMS */
      this.map.addListener("idle", () => {
        const zoom = this.map.getZoom();
        if (zoom > 16) {
          if (!this.overlayWms) this.layerWMS();
        } else {
          this.clearOverlayWms();
        }
      });
    },

    /** Usuwa warstwę kafelkową WMS z kolekcji warstw mapy */
    clearOverlayWms() {
      if (this.overlayWms) {
        const index = this.map.overlayMapTypes
          .getArray()
          .indexOf(this.overlayWms);
        if (index !== -1) {
          this.map.overlayMapTypes.removeAt(index);
        }
        this.overlayWms = null;
      }
    },

    /** Konwertuje Google LatLng na układ współrzędnych 1992 (używany w geoportalu) */
    latlngToEPSG2180(coordinates) {
      const fromProjection = "+proj=longlat +datum=WGS84 +no_defs";
      const toProjection =
        "+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs ";

      return proj4(fromProjection, toProjection, [
        coordinates.lng(),
        coordinates.lat(),
      ]);
    },

    /** Konwertuje współrzędne układu 1992 z powrotem na LatLng (WGS84) */
    EPSG2180Tolatlng(coordinates) {
      const coordinatesArr = coordinates.split(" ");
      const fromProjection =
        "+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs ";
      const toProjection = "+proj=longlat +datum=WGS84 +no_defs";

      return proj4(fromProjection, toProjection, [
        Number(coordinatesArr[0]),
        Number(coordinatesArr[1]),
      ]);
    },

    /** * Implementacja warstwy WMS jako ImageMapType (kafelkowanie).
     * Pobiera działki w małych fragmentach 256x256, co zapewnia płynność i brak błędów BBOX.
     */
    layerWMS() {
      if (this.overlayWms) return;
      this.isWMSLoading = true;

      const wmsOptions = {
        getTileUrl: (coord: any, zoom: number) => {
          const proj = this.map.getProjection();
          const zfactor = Math.pow(2, zoom);

          // Wyznaczanie punktów granicznych dla konkretnego kafle
          const topPoint = new this.google.maps.Point(
            (coord.x * 256) / zfactor,
            (coord.y * 256) / zfactor,
          );
          const botPoint = new this.google.maps.Point(
            ((coord.x + 1) * 256) / zfactor,
            ((coord.y + 1) * 256) / zfactor,
          );

          const topLatLng = proj.fromPointToLatLng(topPoint);
          const botLatLng = proj.fromPointToLatLng(botPoint);

          // Konwersja narożników na EPSG:3857 dla serwera GUGiK
          const swC = this.latLngToEPSG3857(botLatLng.lat(), topLatLng.lng());
          const neC = this.latLngToEPSG3857(topLatLng.lat(), botLatLng.lng());

          const bbox = `${swC[0]},${swC[1]},${neC[0]},${neC[1]}`;

          return (
            "https://integracja02.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow?" +
            "SERVICE=WMS&REQUEST=GetMap&VERSION=1.1.1&LAYERS=dzialki,numery_dzialek" +
            "&SRS=EPSG:3857&WIDTH=256&HEIGHT=256&FORMAT=image/png&TRANSPARENT=TRUE" +
            "&STYLES=&BBOX=" +
            bbox
          );
        },
        tileSize: new this.google.maps.Size(256, 256),
        isPng: true,
        opacity: 1.0,
      };

      this.overlayWms = new this.google.maps.ImageMapType(wmsOptions);
      // Wstawienie warstwy jako nakładki na mapę bazową
      this.map.overlayMapTypes.insertAt(0, this.overlayWms);

      // Ukrywamy loader po krótkim czasie (obrazek wskoczy w tle)
      setTimeout(() => {
        this.isWMSLoading = false;
      }, 1000);
    },

    /** Parsuje string poligonu (WKT) i rysuje obrys działki na mapie */
    drawPlot(polygonString) {
      if (this.polygonLayer) this.polygonLayer.setMap(null);

      let polygonSubString = polygonString.substring(
        polygonString.indexOf("((") + 2,
        polygonString.lastIndexOf("))"),
      );

      polygonSubString = polygonSubString.replace("(", "").replace(")", "");

      const polygonCoord = polygonSubString.split(",");
      const plotCoords = [];
      const bounds = new this.google.maps.LatLngBounds();

      for (const a of polygonCoord) {
        const cordArr = this.EPSG2180Tolatlng(a);
        plotCoords.push({ lat: cordArr[1], lng: cordArr[0] });
        bounds.extend(new this.google.maps.LatLng(cordArr[1], cordArr[0]));
      }

      this.polygonLayer = new this.google.maps.Polygon({
        paths: plotCoords,
        strokeColor: "#0000ff",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#0000ff",
        fillOpacity: 0.35,
      });

      this.polygonLayer.setMap(this.map);

      this.map.setCenter(bounds.getCenter());
      this.map.fitBounds(bounds, 300);
    },

    /** Wyświetla warstwę KML na mapie */
    addLayerToMap(id: number) {
      const layer = this.getLayer(id);
      if (layer) {
        layer.setMap(this.map);
      }
    },

    /** Ukrywa warstwę KML na mapie */
    removeLayerFromMap(id: number) {
      const layer = this.getLayer(id);
      if (layer) layer.setMap(null);
    },

    /** Zwraca obiekt warstwy KML po ID */
    getLayer(id: number): Record<string, any> | null {
      return this.kmlLayers.find((layer) => layer.id === id) || null;
    },

    /** Przełącza tryb pomiaru odległości i czyści tymczasowe markery */
    setMeasurementMode(val: boolean): void {
      if (!val) {
        if (this.poly) this.poly.setMap(null);
        if (this.distanceMarker) this.distanceMarker.setMap(null);
        this.poly = null;
        this.distanceMarker = null;
      }
      this.measurementMode = val;
    },

    /** Obsługuje kliknięcie w dowolne miejsce na mapie (geokodowanie lub pomiar) */
    onMapClick(latLng): void {
      const searchBox = document.querySelector(".search-box") as HTMLElement;
      if (searchBox) searchBox.blur();
      if (!this.measurementMode) {
        this.geocodeLatLng(latLng);
        return;
      }
      if (this.distanceMarker) {
        this.distanceMarker.setPosition(latLng);
      } else {
        this.addDistanceMarker(latLng);
        this.google.maps.event.addListener(this.distanceMarker, "drag", () => {
          this.poly.setPath([
            this.marker.position,
            this.distanceMarker.position,
          ]);
        });
      }
      if (this.poly) {
        this.poly.setPath([this.marker.position, this.distanceMarker.position]);
      } else {
        this.addPoly([this.marker.position, this.distanceMarker.position]);
      }
    },

    /** Dodaje marker główny (lokalizacja inwestycji) */
    addMarker(latLng): void {
      this.marker = new this.google.maps.Marker({
        position: latLng,
        map: this.map,
        draggable: true,
      });
      this.google.maps.event.addListener(this.marker, "drag", () => {
        if (this.circle) this.circle.setCenter(this.marker.position);
        if (this.poly)
          this.poly.setPath([
            this.marker.position,
            this.distanceMarker.position,
          ]);
      });
      this.google.maps.event.addListener(this.marker, "dragend", (e) => {
        this.geocodeLatLng(e.latLng);
      });
    },

    /** Dodaje marker pomocniczy do pomiaru odległości */
    addDistanceMarker(latLng): void {
      this.distanceMarker = new this.google.maps.Marker({
        position: latLng,
        map: this.map,
        draggable: true,
      });
    },

    /** Rysuje linię między dwoma punktami pomiarowymi */
    addPoly(path) {
      this.poly = new this.google.maps.Polyline({
        ...POLY_CONFIG,
        map: this.map,
        path,
      });
    },

    /** Dodaje okrąg wokół głównej lokalizacji (promień analizy) */
    addCircle(latLng): void {
      this.circle = new this.google.maps.Circle({
        center: latLng,
        radius: this.circleRadius,
        map: this.map,
        ...CIRLE_MARKER_CONFIG,
      });
      this.google.maps.event.addListener(this.circle, "click", (e) => {
        if (!this.measurementMode) this.geocodeLatLng(e.latLng);
      });
    },

    /** Pobiera dane adresowe z punktu na mapie przy użyciu Geocodera Google */
    geocodeLatLng(LatLng) {
      this.geocoder.geocode({ location: LatLng }, (results, status) => {
        if (status === "OK" && results[0]) {
          this.marker
            ? this.marker.setPosition(LatLng)
            : this.addMarker(LatLng);
          this.circle ? this.circle.setCenter(LatLng) : this.addCircle(LatLng);
          this.setSelectedPlace(this.formatPlaceData(results[0]));
        }
      });
    },

    /** Formatuje surowe dane z Google Geocoding do struktury aplikacji */
    formatPlaceData(data) {
      const addr = data["address_components"];
      return {
        location: {
          lat: this.marker.position.lat(),
          lng: this.marker.position.lng(),
        },
        address: {
          streetName: this.getAddressType(addr, "route"),
          streetNumber: this.getAddressType(addr, "street_number"),
          postalCode: this.getAddressType(addr, "postal_code"),
          county: this.getAddressType(addr, "administrative_area_level_2"),
          town: this.getAddressType(addr, "locality"),
          municipality: "",
        },
      };
    },

    /** Helper do wyciągania konkretnych typów adresowych (np. kod pocztowy, ulica) */
    getAddressType(address: Array<Record<string, any>>, type: string): string {
      const value = address.find((i) => i.types.includes(type));
      if (value) {
        if (
          (type === "postal_code" && value.long_name.length < 6) ||
          (type === "route" && value.long_name === "Unnamed Road")
        )
          return "";
        return value.long_name;
      }
      return "";
    },
  },
});
